import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Backdrop,
  Button,
  Container,
  CircularProgress,
  Typography,
  TextField,
  Paper,
  Box,
  LinearProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import Tooltip from '@mui/material/Tooltip';
import { alpha } from '@mui/material/styles';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';
import GLExportTable from './GLExportTable';
import GLExportDialog from './GLExportDialog';
import CartwheelTips from './CartwheelTips';

import './GLExport.css';

const DEFAULT_TABLE_HEIGHT = 600;
const MIN_TABLE_HEIGHT = 300;
const MAX_TABLE_HEIGHT = 1000;

const GLExport = () => {
  const { user: firebaseUser, loading: authenticating } = useUserAuth();

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [elapsedTime, setElapsedTime] = useState(0);

  // Cached data from API (full result set)
  const [cachedData, setCachedData] = useState([]);
  const [columns, setColumns] = useState([]);

  // Local filter/sort state (applied to cached data)
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
  // Multi-sort: array of { column, direction } objects
  const [sortConfig, setSortConfig] = useState([
    { column: 'transactiondate', direction: 'asc' },
    { column: 'locationnumber', direction: 'asc' },
    { column: 'memodetail', direction: 'asc' },
  ]);

  // Date filters for API call
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [lastFetchedDates, setLastFetchedDates] = useState({ start: null, end: null });

  // Grouping: 'store' (by store) or 'company' (aggregate all stores)
  const [groupBy, setGroupBy] = useState('store');

  // Fiscal period dropdown
  const [fiscalPeriods, setFiscalPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [loadingPeriods, setLoadingPeriods] = useState(true);

  // Last updated timestamp
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const [tableHeight, setTableHeight] = useState(DEFAULT_TABLE_HEIGHT);

  // Missing days detection
  const [missingDays, setMissingDays] = useState([]);
  const [showMissingDaysAlert, setShowMissingDaysAlert] = useState(true);
  const [missingDaysDialogOpen, setMissingDaysDialogOpen] = useState(false);

  // Fullscreen mode
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  // NetSuite comparison
  const [comparisonData, setComparisonData] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [comparisonError, setComparisonError] = useState(null);
  const [comparisonFilter, setComparisonFilter] = useState(null); // null, 'match', 'variance', 'missing_in_netsuite'
  const [missingInSnowflakeOpen, setMissingInSnowflakeOpen] = useState(false);

  // Accordion states
  const [posJournalExpanded, setPosJournalExpanded] = useState(true); // Expanded by default
  const [cartwheelExpanded, setCartwheelExpanded] = useState(false);

  const timerRef = useRef(null);
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  // Fetch fiscal periods, current period, and last updated timestamp on mount
  useEffect(() => {
    const fetchFiscalData = async () => {
      if (!firebaseUser) return;

      try {
        setLoadingPeriods(true);
        const firebaseUserIdToken = await firebaseUser.getIdToken(true);

        // Fetch all fiscal periods, current period, and last updated in parallel
        const [periodsResponse, currentResponse, lastUpdatedResponse] = await Promise.all([
          axios.get('/hd-api/gl-export/fiscal-periods', {
            headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
          }),
          axios.get('/hd-api/gl-export/current-period', {
            headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
          }),
          axios.get('/hd-api/gl-export/last-updated', {
            headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
          }),
        ]);

        setFiscalPeriods(periodsResponse.data.periods || []);
        setLastUpdated(lastUpdatedResponse.data.lastUpdated);

        // Pre-populate with current period
        if (currentResponse.data) {
          const current = currentResponse.data;
          setSelectedPeriod(current.label);
          // Parse dates as local time to avoid timezone offset issues
          const [startYear, startMonth, startDay] = current.startDate.split('T')[0].split('-');
          const [endYear, endMonth, endDay] = current.endDate.split('T')[0].split('-');
          setStartDate(new Date(startYear, startMonth - 1, startDay));
          setEndDate(new Date(endYear, endMonth - 1, endDay));
        }
      } catch (error) {
        console.error('Error fetching fiscal data:', error);
      } finally {
        setLoadingPeriods(false);
      }
    };

    fetchFiscalData();
  }, [firebaseUser]);

  // Auto-load data when dates are set from fiscal period on initial load
  const initialLoadRef = useRef(false);
  useEffect(() => {
    if (!initialLoadRef.current && startDate && endDate && !loadingPeriods && firebaseUser) {
      initialLoadRef.current = true;
      fetchData();
    }
  }, [startDate, endDate, loadingPeriods, firebaseUser]);

  // Handle fiscal period selection
  const handlePeriodChange = async (event) => {
    const periodLabel = event.target.value;
    setSelectedPeriod(periodLabel);

    if (periodLabel === '') {
      // Custom - don't change dates
      return;
    }

    const period = fiscalPeriods.find((p) => p.label === periodLabel);
    if (period) {
      // Parse dates as local time to avoid timezone offset issues
      const [startYear, startMonth, startDay] = period.startDate.split('T')[0].split('-');
      const [endYear, endMonth, endDay] = period.endDate.split('T')[0].split('-');
      const newStartDate = new Date(startYear, startMonth - 1, startDay);
      const newEndDate = new Date(endYear, endMonth - 1, endDay);
      setStartDate(newStartDate);
      setEndDate(newEndDate);

      // Fetch data with new dates directly (don't wait for state update)
      try {
        setLoading(true);
        setLoadingMessage('Loading GL Export data...');
        const firebaseUserIdToken = await firebaseUser.getIdToken(true);

        const url = `/hd-api/gl-export/all?startDate=${newStartDate.toISOString()}&endDate=${newEndDate.toISOString()}&groupBy=${groupBy}`;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${firebaseUserIdToken}`,
          },
          timeout: 300000,
        });

        const fetchedData = response.data.data || [];
        setCachedData(fetchedData);
        setComparisonData(null);
        setComparisonFilter(null);
        setColumns(response.data.columns || []);
        setLastFetchedDates({ start: newStartDate, end: newEndDate });
        setAppliedSearchQuery(searchQuery);

        // Detect missing days (only for store view)
        if (groupBy === 'store') {
          const missing = detectMissingDays(fetchedData, newStartDate, newEndDate);
          setMissingDays(missing);
          setShowMissingDaysAlert(missing.length > 0);
        } else {
          setMissingDays([]);
          setShowMissingDaysAlert(false);
        }

        setLoading(false);
        setLoadingMessage('');
      } catch (error) {
        console.error('Error fetching GL Export data:', error);
        setCachedData([]);
        setLoading(false);
        setLoadingMessage('');
      }
    }
  };

  // When dates are manually changed, set period to Custom
  const handleStartDateChange = (date) => {
    setStartDate(date);
    setSelectedPeriod('');
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setSelectedPeriod('');
  };

  // Format last updated timestamp for display
  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Refresh data by running the stored procedure
  const handleRefreshData = async () => {
    if (!startDate || !endDate) return;

    try {
      setRefreshing(true);
      setLoading(true);
      setLoadingMessage('Regenerating GL Export data. This may take up to 2 minutes...');
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      const response = await axios.post(
        '/hd-api/gl-export/refresh',
        {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
        },
        {
          headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
          timeout: 300000, // 5 minute timeout
        }
      );

      // Update last updated timestamp
      if (response.data.lastUpdated) {
        setLastUpdated(response.data.lastUpdated);
      }

      // Now fetch the refreshed data
      await fetchData();
    } catch (error) {
      console.error('Error refreshing GL Export data:', error);
      setLoading(false);
      setLoadingMessage('');
    } finally {
      setRefreshing(false);
    }
  };

  // Process cached data with filtering and sorting (memoized for performance)
  const processedData = useMemo(() => {
    let result = [...cachedData];

    // Apply search filter
    if (appliedSearchQuery) {
      const searchLower = appliedSearchQuery.toLowerCase();
      result = result.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(searchLower)
        )
      );
    }

    // Apply multi-sort
    if (sortConfig.length > 0) {
      result.sort((a, b) => {
        for (const { column, direction } of sortConfig) {
          const aVal = a[column] ?? '';
          const bVal = b[column] ?? '';
          let comparison = 0;

          // Handle numeric sorting for amount columns
          if (column === 'debitamount' || column === 'creditamount') {
            const aNum = parseFloat(aVal) || 0;
            const bNum = parseFloat(bVal) || 0;
            comparison = aNum - bNum;
          }
          // Handle date sorting
          else if (column === 'transactiondate') {
            const aDate = new Date(aVal);
            const bDate = new Date(bVal);
            comparison = aDate - bDate;
          }
          // String sorting
          else {
            comparison = String(aVal).localeCompare(String(bVal));
          }

          if (comparison !== 0) {
            return direction === 'asc' ? comparison : -comparison;
          }
        }
        return 0;
      });
    }

    return result;
  }, [cachedData, appliedSearchQuery, sortConfig]);

  // Timer effect for loading indicator
  useEffect(() => {
    if (loading) {
      setElapsedTime(0);
      timerRef.current = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [loading]);

  // Fetch data from API (only when date range or groupBy changes)
  const fetchData = async (overrideGroupBy = null) => {
    if (!startDate || !endDate) {
      return;
    }

    const effectiveGroupBy = overrideGroupBy || groupBy;

    try {
      setLoading(true);
      setLoadingMessage('Loading GL Export data...');
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      // Fetch ALL data from the API (no pagination - we'll handle it locally)
      let url = `/hd-api/gl-export/all?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&groupBy=${effectiveGroupBy}`;
      console.log('Fetching GL data with groupBy:', effectiveGroupBy);

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
        timeout: 300000, // 5 minute timeout for large datasets
      });

      // Cache the full result set
      const fetchedData = response.data.data || [];
      setCachedData(fetchedData);
      setComparisonData(null);
      setComparisonFilter(null);
      setColumns(response.data.columns || []);
      setLastFetchedDates({ start: startDate, end: endDate });
      setAppliedSearchQuery(searchQuery);

      // Detect missing days (only for store view)
      if (effectiveGroupBy === 'store') {
        const missing = detectMissingDays(fetchedData, startDate, endDate);
        setMissingDays(missing);
        setShowMissingDaysAlert(missing.length > 0);
      } else {
        setMissingDays([]);
        setShowMissingDaysAlert(false);
      }

      setLoading(false);
      setLoadingMessage('');
    } catch (error) {
      console.error('Error fetching GL Export data:', error);
      setCachedData([]);
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Handle groupBy change
  const handleGroupByChange = async (event, newGroupBy) => {
    if (newGroupBy !== null) {
      setGroupBy(newGroupBy);
      // Refetch data with new grouping
      if (cachedData.length > 0 || (startDate && endDate)) {
        await fetchData(newGroupBy);
      }
    }
  };

  const handleSearch = () => {
    // Check if we need to fetch new data or just filter cached data
    const datesChanged =
      !lastFetchedDates.start ||
      !lastFetchedDates.end ||
      startDate?.toISOString() !== lastFetchedDates.start?.toISOString() ||
      endDate?.toISOString() !== lastFetchedDates.end?.toISOString();

    if (datesChanged || cachedData.length === 0) {
      // Dates changed or no cached data - fetch from API
      fetchData();
    } else {
      // Just update the search filter (instant, no API call)
      setAppliedSearchQuery(searchQuery);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setAppliedSearchQuery('');
  };

  // Multi-sort handler: Shift+click adds to sort, regular click replaces
  const handleRequestSort = (event, property) => {
    const isShiftHeld = event.shiftKey;

    setSortConfig((prevConfig) => {
      const existingIndex = prevConfig.findIndex((s) => s.column === property);

      if (isShiftHeld) {
        // Shift+click: add to sort or toggle existing
        if (existingIndex >= 0) {
          // Toggle direction or remove if already desc
          const existing = prevConfig[existingIndex];
          if (existing.direction === 'asc') {
            const newConfig = [...prevConfig];
            newConfig[existingIndex] = { column: property, direction: 'desc' };
            return newConfig;
          } else {
            // Remove from sort
            return prevConfig.filter((_, i) => i !== existingIndex);
          }
        } else {
          // Add new sort column
          return [...prevConfig, { column: property, direction: 'asc' }];
        }
      } else {
        // Regular click: replace all sorts with this column
        if (existingIndex >= 0) {
          const existing = prevConfig[existingIndex];
          return [{ column: property, direction: existing.direction === 'asc' ? 'desc' : 'asc' }];
        }
        return [{ column: property, direction: 'asc' }];
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // If we have cached data and dates haven't changed, just filter locally
      if (cachedData.length > 0 &&
          startDate?.toISOString() === lastFetchedDates.start?.toISOString() &&
          endDate?.toISOString() === lastFetchedDates.end?.toISOString()) {
        setAppliedSearchQuery(searchQuery);
      } else {
        handleSearch();
      }
    }
  };

  const handleExport = async () => {
    if (!startDate || !endDate) {
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage('Exporting GL data to Excel. This may take up to 2 minutes...');
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      let url = `/hd-api/gl-export/export?sortConfig=${encodeURIComponent(JSON.stringify(sortConfig))}&groupBy=${groupBy}`;

      if (searchQuery) {
        url += `&searchQuery=${encodeURIComponent(searchQuery)}`;
      }
      if (startDate) {
        url += `&startDate=${startDate.toISOString()}`;
      }
      if (endDate) {
        url += `&endDate=${endDate.toISOString()}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${firebaseUserIdToken}`,
        },
        responseType: 'blob',
        timeout: 180000, // 3 minute timeout
      });

      // Create download link
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `gl-export-${startDate.toISOString().split('T')[0]}-to-${endDate.toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setLoading(false);
      setLoadingMessage('');
    } catch (error) {
      console.error('Error exporting GL data:', error);
      setLoading(false);
      setLoadingMessage('');
    }
  };

  // Compare Snowflake data with NetSuite
  const handleCompareToNetsuite = async () => {
    if (comparisonData) {
      setComparisonData(null);
      setComparisonError(null);
      setComparisonFilter(null);
      return;
    }

    try {
      setComparing(true);
      setComparisonError(null);
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);
      const response = await axios.get(
        `/hd-api/gl-export/compare-netsuite?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`,
        {
          headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
          timeout: 300000, // 5 min timeout
        }
      );
      setComparisonData(response.data);
    } catch (error) {
      console.error('Error comparing to NetSuite:', error);
      setComparisonError(error.response?.data?.error || 'Failed to compare with NetSuite');
    } finally {
      setComparing(false);
    }
  };

  // Format elapsed time as mm:ss
  const formatElapsedTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Detect missing days in the data
  const detectMissingDays = (data, start, end) => {
    if (!data || data.length === 0 || !start || !end) {
      return [];
    }

    // Get all unique dates from the data (transactiondate is in MM/DD/YYYY format)
    const datesInData = new Set();
    data.forEach((row) => {
      if (row.transactiondate) {
        // Parse MM/DD/YYYY format
        const parts = row.transactiondate.split('/');
        if (parts.length === 3) {
          // Normalize to YYYY-MM-DD for comparison
          const normalized = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
          datesInData.add(normalized);
        }
      }
    });

    // Generate all dates in the range, excluding today and future dates
    const missing = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison

    while (currentDate <= endDate) {
      // Skip today and future dates
      if (currentDate >= today) {
        currentDate.setDate(currentDate.getDate() + 1);
        continue;
      }

      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      if (!datesInData.has(dateStr)) {
        // Format as MM/DD/YYYY for display
        missing.push(`${month}/${day}/${year}`);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return missing;
  };

  // Format missing days for display (group consecutive ranges)
  const formatMissingDays = (days) => {
    if (days.length === 0) return '';
    if (days.length <= 5) return days.join(', ');

    // Show first 3 and indicate how many more
    return `${days.slice(0, 3).join(', ')} and ${days.length - 3} more`;
  };

  // Regenerate data for a specific day or all missing days
  const handleRegenerateDay = async (day) => {
    if (!firebaseUser) return;

    try {
      setRefreshing(true);
      const firebaseUserIdToken = await firebaseUser.getIdToken(true);

      let regenerateStart, regenerateEnd;

      if (day === null) {
        // Regenerate all missing days - use full date range
        regenerateStart = startDate;
        regenerateEnd = endDate;
      } else {
        // Parse MM/DD/YYYY format and create date for single day
        const parts = day.split('/');
        const singleDate = new Date(parts[2], parts[0] - 1, parts[1]);
        regenerateStart = singleDate;
        regenerateEnd = singleDate;
      }

      const response = await axios.post(
        '/hd-api/gl-export/refresh',
        {
          startDate: regenerateStart.toISOString(),
          endDate: regenerateEnd.toISOString(),
        },
        {
          headers: { Authorization: `Bearer ${firebaseUserIdToken}` },
          timeout: 300000, // 5 minute timeout
        }
      );

      // Update last updated timestamp
      if (response.data.lastUpdated) {
        setLastUpdated(response.data.lastUpdated);
      }

      // Refresh the data to see updated results
      await fetchData();

      // Close dialog if no more missing days
      if (missingDays.length === 0) {
        setMissingDaysDialogOpen(false);
      }
    } catch (error) {
      console.error('Error regenerating data:', error);
    } finally {
      setRefreshing(false);
    }
  };

  let content;
  if (authenticating) {
    content = (
      <div style={{ marginTop: '10%', width: '100%', textAlign: 'center' }}>
        <CircularProgress />
      </div>
    );
  } else {
    content = (
      <div style={{ marginTop: 16 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            mb: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                borderRadius: 2,
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato', color: 'text.primary' }}>
                GL Export
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Export general ledger data for accounting
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Last updated
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                {formatLastUpdated(lastUpdated)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                Auto-refreshes hourly for previous day
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* POS Journal Entries - Collapsible Section */}
        <Accordion
          expanded={posJournalExpanded}
          onChange={(e, expanded) => setPosJournalExpanded(expanded)}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
              borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AccountBalanceIcon sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                POS Journal Entries
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {/* Toolbar Section */}
            <Box
              sx={{
                px: 3,
                py: 2,
                backgroundColor: (theme) => theme.palette.grey[50],
              }}
            >
              {/* Row 1: Group toggle, Fiscal Period, Date pickers, buttons */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Tooltip title="View data by individual store or aggregated company-wide" arrow>
                  <ToggleButtonGroup
                    value={groupBy}
                    exclusive
                    onChange={handleGroupByChange}
                    size="small"
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiToggleButton-root': {
                        px: 1.5,
                        py: 0.5,
                      },
                    }}
                  >
                    <ToggleButton value="store" aria-label="by store">
                      <StorefrontIcon sx={{ mr: 0.5, fontSize: 18 }} />
                      By Store
                    </ToggleButton>
                    <ToggleButton value="company" aria-label="company-wide">
                      <BusinessIcon sx={{ mr: 0.5, fontSize: 18 }} />
                      Company
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Tooltip>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Fiscal Period</InputLabel>
                  <Select
                    value={selectedPeriod}
                    onChange={handlePeriodChange}
                    label="Fiscal Period"
                    disabled={loadingPeriods}
                  >
                    <MenuItem value="">
                      <em>Custom</em>
                    </MenuItem>
                    {fiscalPeriods.map((period) => (
                      <MenuItem key={period.label} value={period.label}>
                        {period.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <DatePicker
                  selected={startDate}
                  onChange={handleStartDateChange}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  placeholderText="Start Date"
                  customInput={
                    <TextField size="small" label="Start Date" sx={{ width: 140 }} autoComplete="off" />
                  }
                />
                <DatePicker
                  selected={endDate}
                  onChange={handleEndDateChange}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  placeholderText="End Date"
                  customInput={
                    <TextField size="small" label="End Date" sx={{ width: 140 }} autoComplete="off" />
                  }
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Load data from cached results (fast)" arrow>
                    <span>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleSearch}
                        disabled={!startDate || !endDate || loading}
                        startIcon={<PlayArrowIcon />}
                      >
                        Load
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Regenerate data for selected date range (slow, ~2 min)" arrow>
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleRefreshData}
                        disabled={!startDate || !endDate || loading}
                        startIcon={<RefreshIcon />}
                        color="warning"
                      >
                        Regenerate
                      </Button>
                    </span>
                  </Tooltip>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setExportDialogOpen(true)}
                    disabled={cachedData.length === 0}
                    startIcon={<DownloadIcon />}
                  >
                    Export
                  </Button>
                  <Tooltip title={groupBy === 'company' ? 'NetSuite comparison requires Store view' : ''} arrow>
                    <span>
                      <Button
                        variant={comparisonData ? 'contained' : 'outlined'}
                        size="small"
                        onClick={handleCompareToNetsuite}
                        disabled={cachedData.length === 0 || comparing || groupBy === 'company'}
                        startIcon={comparing ? <CircularProgress size={16} /> : <CompareArrowsIcon />}
                        color={comparisonData ? 'secondary' : 'primary'}
                      >
                        {comparing ? 'Comparing...' : comparisonData ? 'Clear Comparison' : 'Compare to NetSuite'}
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Toggle fullscreen" arrow>
                    <IconButton
                      size="small"
                      onClick={() => setIsFullscreen(true)}
                      sx={{ ml: 1 }}
                    >
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

            </Box>

            {/* Missing Days Alert */}
            <Collapse in={missingDays.length > 0 && showMissingDaysAlert}>
              <Alert
                severity="warning"
                icon={<WarningAmberIcon />}
                onClose={() => setShowMissingDaysAlert(false)}
                sx={{ borderRadius: 0 }}
              >
                <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
                  Missing data for {missingDays.length} day{missingDays.length > 1 ? 's' : ''}:
                </Typography>{' '}
                <Typography variant="body2" component="span">
                  {formatMissingDays(missingDays)}
                </Typography>
                {missingDays.length > 5 && (
                  <Typography
                    variant="body2"
                    component="span"
                    onClick={() => setMissingDaysDialogOpen(true)}
                    sx={{ ml: 1, textDecoration: 'underline', cursor: 'pointer', color: 'primary.main' }}
                  >
                    (view all)
                  </Typography>
                )}
              </Alert>
            </Collapse>

            {/* NetSuite Comparison Summary */}
            <Collapse in={!!comparisonData}>
              <Alert
                severity="info"
                icon={<CompareArrowsIcon />}
                onClose={() => { setComparisonData(null); setComparisonError(null); setComparisonFilter(null); }}
                sx={{ borderRadius: 0 }}
              >
                <Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
                  NetSuite Comparison:
                </Typography>{' '}
                <Typography
                  variant="body2"
                  component="span"
                  onClick={() => setComparisonFilter(comparisonFilter === 'match' ? null : 'match')}
                  sx={{
                    color: 'success.main',
                    cursor: 'pointer',
                    textDecoration: comparisonFilter === 'match' ? 'underline' : 'none',
                    fontWeight: comparisonFilter === 'match' ? 'bold' : 'normal',
                  }}
                >
                  {comparisonData?.summary?.matched || 0} matched
                </Typography>
                {' | '}
                <Typography
                  variant="body2"
                  component="span"
                  onClick={() => setComparisonFilter(comparisonFilter === 'variance' ? null : 'variance')}
                  sx={{
                    color: 'warning.main',
                    cursor: 'pointer',
                    textDecoration: comparisonFilter === 'variance' ? 'underline' : 'none',
                    fontWeight: comparisonFilter === 'variance' ? 'bold' : 'normal',
                  }}
                >
                  {comparisonData?.summary?.variances || 0} variances
                </Typography>
                {' | '}
                <Typography
                  variant="body2"
                  component="span"
                  onClick={() => setComparisonFilter(comparisonFilter === 'missing_in_netsuite' ? null : 'missing_in_netsuite')}
                  sx={{
                    color: 'error.main',
                    cursor: 'pointer',
                    textDecoration: comparisonFilter === 'missing_in_netsuite' ? 'underline' : 'none',
                    fontWeight: comparisonFilter === 'missing_in_netsuite' ? 'bold' : 'normal',
                  }}
                >
                  {comparisonData?.summary?.missingInNetsuite || 0} missing in NetSuite
                </Typography>
                {' | '}
                <Typography
                  variant="body2"
                  component="span"
                  onClick={() => setMissingInSnowflakeOpen(true)}
                  sx={{
                    color: 'error.main',
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {comparisonData?.summary?.missingInSnowflake || 0} missing in Snowflake
                </Typography>
                {' | '}
                <Typography variant="body2" component="span">
                  {comparisonData?.summary?.totalRows || 0} total rows
                </Typography>
                {comparisonData?.summary?.suppressedToday > 0 && (
                  <>
                    {' | '}
                    <Typography variant="body2" component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                      {comparisonData.summary.suppressedToday} yesterday's rows skipped (NS import pending)
                    </Typography>
                  </>
                )}
                {comparisonFilter && (
                  <Typography
                    variant="body2"
                    component="span"
                    onClick={() => setComparisonFilter(null)}
                    sx={{ ml: 1, cursor: 'pointer', color: 'text.secondary', textDecoration: 'underline' }}
                  >
                    (clear filter)
                  </Typography>
                )}
              </Alert>
            </Collapse>

            {/* Comparison Error */}
            <Collapse in={!!comparisonError}>
              <Alert
                severity="error"
                onClose={() => setComparisonError(null)}
                sx={{ borderRadius: 0 }}
              >
                {comparisonError}
              </Alert>
            </Collapse>

            {/* Data Table */}
            <GLExportTable
              data={processedData}
              columns={columns}
              totalCount={processedData.length}
              sortConfig={sortConfig}
              onRequestSort={handleRequestSort}
              tableHeight={tableHeight}
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onSearchClear={handleClear}
              onTableHeightChange={setTableHeight}
              minTableHeight={MIN_TABLE_HEIGHT}
              maxTableHeight={MAX_TABLE_HEIGHT}
              firebaseUser={firebaseUser}
              groupBy={groupBy}
              startDate={startDate}
              endDate={endDate}
              comparisonData={comparisonData}
              comparisonFilter={comparisonFilter}
            />
          </AccordionDetails>
        </Accordion>

        {/* Delivery Partner Tips (Cartwheel) - Collapsible Section */}
        <Accordion
          expanded={cartwheelExpanded}
          onChange={(e, expanded) => setCartwheelExpanded(expanded)}
          sx={{
            mt: 2,
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: '16px 0 0 0' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.04),
              borderLeft: (theme) => `4px solid ${theme.palette.info.main}`,
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <LocalShippingIcon sx={{ color: 'info.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Delivery Partner Tips (Cartwheel)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            {cartwheelExpanded && startDate && endDate && (
              <CartwheelTips
                startDate={startDate}
                endDate={endDate}
                firebaseUser={firebaseUser}
              />
            )}
            {cartwheelExpanded && (!startDate || !endDate) && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Please select a date range above to view delivery partner tips.
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </div>
    );
  }

  // Fullscreen mode render
  if (isFullscreen) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Fullscreen header bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#fafafa',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AccountBalanceIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              GL Export
            </Typography>
            {selectedPeriod && (
              <Typography variant="body2" color="text.secondary">
                {selectedPeriod}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ToggleButtonGroup
              value={groupBy}
              exclusive
              onChange={handleGroupByChange}
              size="small"
            >
              <ToggleButton value="store">
                <StorefrontIcon sx={{ mr: 0.5, fontSize: 18 }} />
                By Store
              </ToggleButton>
              <ToggleButton value="company">
                <BusinessIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Company
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Exit fullscreen" arrow>
              <IconButton onClick={() => setIsFullscreen(false)}>
                <FullscreenExitIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Table fills remaining space */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          <GLExportTable
            data={processedData}
            columns={columns}
            totalCount={processedData.length}
            sortConfig={sortConfig}
            onRequestSort={handleRequestSort}
            tableHeight="100%"
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSearchClear={handleClear}
            onTableHeightChange={setTableHeight}
            minTableHeight={MIN_TABLE_HEIGHT}
            maxTableHeight={MAX_TABLE_HEIGHT}
            firebaseUser={firebaseUser}
            groupBy={groupBy}
            isFullscreen={true}
            startDate={startDate}
            endDate={endDate}
            comparisonData={comparisonData}
            comparisonFilter={comparisonFilter}
          />
        </Box>

        {/* Loading backdrop */}
        <Backdrop
          sx={{ color: '#fff', zIndex: 1400 }}
          open={loading}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              padding: 4,
              borderRadius: 2,
              minWidth: 300,
            }}
          >
            <Typography variant="h6" sx={{ textAlign: 'center' }}>
              {loadingMessage || 'Loading...'}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              Elapsed time: {formatElapsedTime(elapsedTime)}
            </Typography>
            <LinearProgress sx={{ width: '100%', mt: 2 }} color="inherit" />
          </Box>
        </Backdrop>
      </Box>
    );
  }

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />
        {content}
      </Container>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 4,
            borderRadius: 2,
            minWidth: 300,
          }}
        >
          <Typography variant="h6" sx={{ textAlign: 'center' }}>
            {loadingMessage || 'Loading...'}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            Elapsed time: {formatElapsedTime(elapsedTime)}
          </Typography>
          <LinearProgress
            sx={{ width: '100%', mt: 2 }}
            color="inherit"
          />
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
            Please wait, do not close this page
          </Typography>
        </Box>
      </Backdrop>

      {/* Missing Days Dialog */}
      <Dialog
        open={missingDaysDialogOpen}
        onClose={() => setMissingDaysDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Missing Days ({missingDays.length})</span>
          <Button
            variant="contained"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => handleRegenerateDay(null)}
            disabled={refreshing}
            color="warning"
          >
            Regenerate All
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {refreshing && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" icon={<CircularProgress size={20} />}>
                Regenerating data... This may take a few minutes.
              </Alert>
            </Box>
          )}
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {missingDays.map((day) => (
              <ListItem
                key={day}
                sx={{ py: 0.5 }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleRegenerateDay(day)}
                    disabled={refreshing}
                    title={`Regenerate ${day}`}
                    sx={{ color: 'warning.main' }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemText primary={day} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingDaysDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Missing in Snowflake Dialog */}
      <Dialog
        open={missingInSnowflakeOpen}
        onClose={() => setMissingInSnowflakeOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Missing in Snowflake ({comparisonData?.missingInSnowflake?.length || 0} rows)</DialogTitle>
        <DialogContent>
          {comparisonData?.missingInSnowflake?.length > 0 ? (
            <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>External ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Memo</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonData.missingInSnowflake.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{item.externalId}</TableCell>
                      <TableCell>{item.memo}</TableCell>
                      <TableCell align="right">
                        ${Number(item.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">No rows missing in Snowflake.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMissingInSnowflakeOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <GLExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        firebaseUser={firebaseUser}
        initialStartDate={startDate}
        initialEndDate={endDate}
        initialGroupBy={groupBy}
        data={cachedData}
        comparisonData={comparisonData}
      />
    </div>
  );
};

export default GLExport;
